
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarIcon, Upload, X, FileText, AlertCircle, Loader2, FileUp, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";

export interface CurrentAffairWordUploaderProps {
  onSuccess: () => void;
}

const CurrentAffairWordUploader: React.FC<CurrentAffairWordUploaderProps> = ({ onSuccess }) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState<Date | undefined>();
  const [timePeriod, setTimePeriod] = useState('daily');
  const [language, setLanguage] = useState('english');
  const [tags, setTags] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const { userId, isAuthenticated } = useAuthSession();
  const [processingPhase, setProcessingPhase] = useState<'none' | 'extracting' | 'basic' | 'enhanced'>('none');
  
  const languages = [
    { value: "english", label: "English" },
    { value: "hindi", label: "Hindi" },
    { value: "tamil", label: "Tamil" },
    { value: "telugu", label: "Telugu" },
    { value: "kannada", label: "Kannada" },
    { value: "malayalam", label: "Malayalam" },
    { value: "marathi", label: "Marathi" },
    { value: "gujarati", label: "Gujarati" },
    { value: "punjabi", label: "Punjabi" },
    { value: "bengali", label: "Bengali" },
    { value: "urdu", label: "Urdu" }
  ];
  
  const timePeriods = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" }
  ];
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      if (
        selectedFile.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || // .docx
        selectedFile.type === "application/msword" // .doc
      ) {
        setFile(selectedFile);
        
        const fileName = selectedFile.name.replace(/\.[^/.]+$/, ""); // Remove extension
        setTitle(fileName);
        
        extractContentFromFile(selectedFile);
      } else {
        toast.error("Please upload a Word document (.doc or .docx)");
        e.target.value = "";
      }
    }
  };
  
  const extractContentFromFile = async (file: File) => {
    setIsExtracting(true);
    setExtractedText('');
    setProcessingPhase('extracting');
    
    try {
      if (!isAuthenticated) {
        toast.error("You must be logged in to upload content");
        setIsExtracting(false);
        setProcessingPhase('none');
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("Authentication session not found");
        setIsExtracting(false);
        setProcessingPhase('none');
        return;
      }
      
      console.log("Sending request to process Word file (Phase 1)...");
      
      try {
        const response = await fetch(
          'https://fvoskhrltpygiduhzcgq.supabase.co/functions/v1/process-word-file',
          {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          }
        );
        
        if (!response.ok) {
          let errorMessage = 'Failed to process document';
          try {
            const errorText = await response.text();
            console.error("Error response from Edge Function:", errorText);
            errorMessage = errorText || `Failed to process document - server responded with status ${response.status}`;
          } catch (err) {
            console.error("Error reading error response:", err);
          }
          throw new Error(errorMessage);
        }
        
        const result = await response.json();
        console.log("Phase 1 processing result:", result);
        
        if (result.html && result.html.length > 0) {
          setExtractedText(result.html);
          setProcessingPhase('basic');
          toast.success("Content extracted successfully");
          console.log("HTML content extracted successfully, length:", result.html.length);
        } else if (result.error) {
          toast.warning("Document processed with errors: " + result.error);
          setExtractedText("<p>Error during processing: " + result.error + "</p>");
          setProcessingPhase('none');
        } else {
          toast.warning("Document processed but no content was extracted");
          setExtractedText("<p>No content could be extracted from the document.</p>");
          setProcessingPhase('none');
        }
      } catch (fetchError) {
        console.error("Fetch error:", fetchError);
        toast.error("Network error: " + (fetchError instanceof Error ? fetchError.message : String(fetchError)));
        setExtractedText("<p>Error: Failed to communicate with the processing service. Please try again.</p>");
        setProcessingPhase('none');
      }
    } catch (error) {
      console.error("Error extracting content:", error);
      toast.error("Failed to extract content: " + (error instanceof Error ? error.message : String(error)));
      setExtractedText("<p>Error processing document. Please try again.</p>");
      setProcessingPhase('none');
    } finally {
      setIsExtracting(false);
    }
  };
  
  const triggerEnhancedProcessing = async (contentId: string) => {
    try {
      if (!file || !contentId) return;
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error("Authentication session not found for enhanced processing");
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('contentId', contentId);
      
      console.log("Triggering Phase 2 (enhanced) processing for content ID:", contentId);
      
      fetch(
        'https://fvoskhrltpygiduhzcgq.supabase.co/functions/v1/process-word-file',
        {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      ).then(response => {
        if (response.ok) {
          console.log("Phase 2 processing initiated successfully");
        } else {
          console.error("Failed to initiate Phase 2 processing:", response.status);
        }
      }).catch(error => {
        console.error("Error in Phase 2 processing:", error);
      });
    } catch (error) {
      console.error("Error triggering enhanced processing:", error);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    if (!extractedText && !file) {
      toast.error("Please upload a Word document");
      return;
    }
    
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    
    if (!isAuthenticated || !userId) {
      toast.error("You must be logged in to upload content");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const tagsList = tags.split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      // Format date as YYYY-MM-DD to avoid timezone issues
      const formattedDate = format(date, 'yyyy-MM-dd');
      
      const hasContent = extractedText && extractedText.length > 20;
      
      const currentAffair = {
        title,
        content: hasContent ? extractedText : "<p>Content is being processed</p>",
        date: formattedDate, // Store only the date part without time
        time_period: timePeriod,
        language,
        category: 'Current Affairs',
        created_by: userId,
        processing_status: hasContent ? 'processing' : 'pending',
        source: file?.name,
        metadata: {
          tags: tagsList,
          source_type: "word_document",
          file_name: file?.name,
          file_type: file?.type,
          file_size: file?.size,
          original_date: formattedDate, // Use formatted date here too
          phase: 'basic',
          enhanced: false
        }
      };
      
      console.log("Saving current affair:", currentAffair);
      
      const { data, error } = await supabase
        .from('current_affairs')
        .insert([currentAffair])
        .select();
      
      if (error) {
        console.error("Error uploading current affair:", error);
        toast.error("Failed to upload content: " + error.message);
        return;
      }
      
      console.log("Current affair created from Word document:", data);
      
      if (data && data.length > 0 && data[0].id) {
        triggerEnhancedProcessing(data[0].id);
      }
      
      toast.success("Word document processed and content uploaded successfully. Enhanced processing is running in the background.");
      
      resetForm();
      
      onSuccess();
    } catch (error) {
      console.error("Error uploading current affair:", error);
      toast.error("Failed to upload content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetForm = () => {
    setTitle('');
    setFile(null);
    setDate(undefined);
    setTimePeriod('daily');
    setLanguage('english');
    setTags('');
    setExtractedText('');
    setProcessingPhase('none');
    
    const fileInput = document.getElementById('document-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center p-4">
            <AlertCircle className="h-10 w-10 text-orange-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Authentication Required</h3>
            <p className="text-muted-foreground mb-4">
              You need to be logged in to upload content.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Upload Word Document</CardTitle>
          <CardDescription>Extract current affairs content from Word documents</CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="document-upload">Upload Word Document</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="document-upload"
                    type="file"
                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    className="flex-1"
                    disabled={isExtracting || isLoading}
                  />
                </div>
                
                {file && (
                  <div className="flex items-center text-sm text-muted-foreground mt-1">
                    <FileText className="h-4 w-4 mr-1" />
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </div>
                )}
              </div>
              
              {processingPhase !== 'none' && (
                <div className={`rounded-md p-3 ${
                  processingPhase === 'extracting' 
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' 
                    : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                }`}>
                  <div className="flex items-center">
                    {processingPhase === 'extracting' ? (
                      <Loader2 className="h-4 w-4 text-yellow-600 dark:text-yellow-400 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                    )}
                    <div>
                      <p className="text-sm font-medium">
                        {processingPhase === 'extracting' 
                          ? 'Extracting content from document...' 
                          : 'Basic content extracted successfully'}
                      </p>
                      {processingPhase === 'basic' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          After saving, enhanced processing will run in the background to improve content and extract questions.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input 
                  id="title" 
                  placeholder="Enter the title" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="timePeriod">Time Period</Label>
                  <Select value={timePeriod} onValueChange={setTimePeriod}>
                    <SelectTrigger id="timePeriod">
                      <SelectValue placeholder="Select time period" />
                    </SelectTrigger>
                    <SelectContent>
                      {timePeriods.map((period) => (
                        <SelectItem key={period.value} value={period.value}>
                          {period.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger id="language">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    placeholder="national, elections, economy, etc."
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                  />
                </div>
              </div>
              
              {isExtracting ? (
                <div className="border rounded-lg p-4 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
                  <span>Extracting content from document...</span>
                </div>
              ) : extractedText ? (
                <div className="space-y-2">
                  <Label>Extracted Content Preview</Label>
                  <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                    <div dangerouslySetInnerHTML={{ __html: extractedText }} className="prose prose-sm max-w-none" />
                  </div>
                </div>
              ) : null}
            </div>
          </form>
        </CardContent>
        
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={resetForm} disabled={isLoading || isExtracting}>
            <X className="h-4 w-4 mr-2" /> Reset
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading || isExtracting || !file || !date || !title.trim() || !userId}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
              </>
            ) : (
              <>
                <FileUp className="h-4 w-4 mr-2" /> Upload Document
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export { CurrentAffairWordUploader };
